import { useState, type FormEvent } from 'react'
import { Link } from 'react-router-dom'

import { Badge, PageHeader, State } from '../components/UI'
import { api, businessPath, money } from '../lib/api'


type ResourceType =
  | 'product'
  | 'customer'
  | 'supplier'
  | 'sale'
  | 'purchase'
  | 'invoice'
  | 'quote'
  | 'staff'


type SearchResult = {
  resource_type: ResourceType
  id: string
  display: string
  secondary: string
}


type SearchResponse = {
  query: string
  results: SearchResult[]
}


const order: ResourceType[] = [
  'product',
  'customer',
  'supplier',
  'sale',
  'purchase',
  'invoice',
  'quote',
  'staff',
]


const titles: Record<ResourceType, string> = {
  product: 'Products',
  customer: 'Customers',
  supplier: 'Suppliers',
  sale: 'Sales',
  purchase: 'Purchases',
  invoice: 'Invoices',
  quote: 'Quotes',
  staff: 'Staff',
}


const paths: Record<ResourceType, string> = {
  product: '/products',
  customer: '/customers',
  supplier: '/suppliers',
  sale: '/sales',
  purchase: '/purchases',
  invoice: '/invoices',
  quote: '/quotes',
  staff: '/staff',
}


const financial = new Set<ResourceType>([
  'sale',
  'purchase',
  'invoice',
  'quote',
])


const secondaryText = (result: SearchResult) => {
  if (!result.secondary) return '?'

  if (
    financial.has(result.resource_type) &&
    Number.isFinite(Number(result.secondary))
  ) {
    return money(result.secondary)
  }

  return result.secondary
}


export default function GlobalSearchPage() {
  const [query, setQuery] = useState('')
  const [searched, setSearched] = useState('')
  const [results, setResults] = useState<SearchResult[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')


  const search = async (event?: FormEvent) => {
    event?.preventDefault()

    const term = query.trim()

    if (term.length < 2) {
      setError('Enter at least two characters.')
      setResults([])
      setSearched('')
      return
    }

    setLoading(true)
    setError('')

    try {
      const response = await api<SearchResponse>(
        businessPath(
          `/search?q=${encodeURIComponent(term)}&limit=80`,
        ),
      )

      setResults(response.results)
      setSearched(response.query)
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Unable to search this workspace.',
      )
      setResults([])
    } finally {
      setLoading(false)
    }
  }


  const clear = () => {
    setQuery('')
    setSearched('')
    setResults([])
    setError('')
  }


  const groups = order
    .map((type) => ({
      type,
      results: results.filter(
        (result) => result.resource_type === type,
      ),
    }))
    .filter((group) => group.results.length)


  return (
    <section className="aad-page">
      <PageHeader
        title="Global Search"
        description="Search your workspace from one place."
      />

      <form
        className="aad-card aad-card-padding"
        onSubmit={search}
      >
        <div className="resource-toolbar">
          <input
            className="aad-input"
            autoFocus
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search product, customer, supplier, receipt, invoice, quote or staff"
            aria-label="Global search"
          />

          <button
            className="aad-button aad-button-gold"
            disabled={loading}
          >
            {loading ? 'Searching?' : 'Search'}
          </button>

          {(query || searched) && (
            <button
              type="button"
              className="aad-button aad-button-secondary"
              onClick={clear}
              disabled={loading}
            >
              Clear
            </button>
          )}
        </div>

        <p className="aad-muted">
          Search by product name, SKU, barcode, customer or
          supplier details, receipt/reference number, invoice
          number, quote number, or staff name/email when your
          role has staff access.
        </p>
      </form>

      {error && (
        <p className="aad-error" role="alert">
          {error}
        </p>
      )}

      {loading ? (
        <State title="Searching workspace?" />
      ) : searched && !results.length ? (
        <State title={`No results for "${searched}"`}>
          Try another name, number, SKU, barcode, email or
          reference.
        </State>
      ) : !searched ? (
        <State title="Search your business">
          Enter at least two characters to begin.
        </State>
      ) : (
        <>
          <div className="kpi-grid">
            <article className="aad-card aad-card-padding">
              <small>Search</small>
              <strong>{searched}</strong>
            </article>

            <article className="aad-card aad-card-padding">
              <small>Results</small>
              <strong>{results.length}</strong>
            </article>

            <article className="aad-card aad-card-padding">
              <small>Resource types</small>
              <strong>{groups.length}</strong>
            </article>
          </div>

          {groups.map((group) => (
            <section
              className="aad-card aad-card-padding"
              key={group.type}
            >
              <div className="header-actions">
                <h2 className="aad-section-title">
                  {titles[group.type]}
                </h2>

                <Badge value={group.results.length} />
              </div>

              <div className="aad-table-wrap">
                <table className="aad-table">
                  <thead>
                    <tr>
                      <th>Result</th>
                      <th>Details</th>
                      <th>Type</th>
                      <th>Open</th>
                    </tr>
                  </thead>

                  <tbody>
                    {group.results.map((result) => (
                      <tr
                        key={`${result.resource_type}-${result.id}`}
                      >
                        <td>
                          <strong>{result.display}</strong>
                        </td>

                        <td>
                          {secondaryText(result)}
                        </td>

                        <td>
                          <Badge
                            value={result.resource_type}
                          />
                        </td>

                        <td>
                          <Link
                            className="aad-button aad-button-secondary"
                            to={`${paths[result.resource_type]}/${result.id}`}
                          >
                            Open
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </section>
          ))}
        </>
      )}
    </section>
  )
}
